import { useState } from 'react'

import { useChatTurnCompletion } from '../contexts/ChatTurnCompletionContext'
import type { TripChatMobileTabId } from './TripChatMobileTabs'

interface UseTripChatMobileUnreadChatArgs {
  activeTab: TripChatMobileTabId
  chatId: number | undefined
}

/**
 * Mobile-only unread marker for the Chat tab.
 *
 * Desktop keeps the conversation on screen next to the plan, so a reply there is never
 * missed and this never runs. On mobile the chat is one tab of three, and a turn can
 * finish while the traveller is reading the List or Map.
 *
 * "Unread" is derived rather than stored: the only thing worth remembering is which turn
 * the traveller has already caught up on. Both updates adjust state during render — the
 * pattern `useItineraryUpdates` already uses for its refetch interval — so the value is
 * correct on the render that needs it, with no effect round-trip.
 */
export default function useTripChatMobileUnreadChat({
  activeTab,
  chatId,
}: UseTripChatMobileUnreadChatArgs): boolean {
  const { lastCompletedTurnId } = useChatTurnCompletion()
  const [seenChatId, setSeenChatId] = useState(chatId)
  const [seenTurnId, setSeenTurnId] = useState(lastCompletedTurnId)

  if (seenChatId !== chatId) {
    // Whatever had completed belongs to the conversation being left, so it is already
    // caught up on as far as the one being opened is concerned.
    setSeenChatId(chatId)
    setSeenTurnId(lastCompletedTurnId)
  } else if (activeTab === 'chat' && seenTurnId !== lastCompletedTurnId) {
    setSeenTurnId(lastCompletedTurnId)
  }

  // A turn that ended without an assistant message — an error, say — leaves nothing to
  // catch up on, so it never raises the dot.
  return activeTab !== 'chat'
    && lastCompletedTurnId !== undefined
    && lastCompletedTurnId !== seenTurnId
}
